/**
 * Live product data for product links inside blog articles.
 *
 * - Product entries (a product link inside <strong>/<b>, e.g. the bold product
 *   names in a recommendation list) get the current price appended: "… · 69,90 €".
 * - On hover or keyboard focus, any product link shows a small card with photo,
 *   name, price and an add-to-cart button (with variant choice for e.g. Gold/Silber).
 *   The card is inserted right after the link, so Tab moves from the link into it.
 *
 * Everything is fetched live from /products/<handle>.js, so prices never go stale.
 * Links to products that no longer exist (archived/unpublished) get neither.
 * Touch devices only get the inline price: tapping a link opens the product.
 * Strings come from locales (blogs.product_preview.*), rendered by main-article.liquid.
 */
(() => {
  const links = [...document.querySelectorAll('.article--main .rte a[href*="/products/"]')];
  if (!links.length) return;

  const stringsEl = document.getElementById('ArticleProductPreviewStrings');
  const strings = stringsEl ? JSON.parse(stringsEl.textContent) : {};
  const t = (key, fallback) => strings[key] || fallback;

  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  const currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
  const money = new Intl.NumberFormat(document.documentElement.lang || 'de-DE', { style: 'currency', currency });
  const formatMoney = (cents) => money.format(cents / 100);
  const cache = new Map();

  const handleFromLink = (link) => {
    let url;
    try {
      url = new URL(link.href, window.location.origin);
    } catch (e) {
      return null;
    }
    const bareHost = (host) => host.replace(/^www\./, '');
    if (bareHost(url.host) !== bareHost(window.location.host)) return null;
    const match = url.pathname.match(/\/products\/([^/?#]+)/);
    return match ? decodeURIComponent(match[1]) : null;
  };

  const fetchProduct = (handle) => {
    if (!cache.has(handle)) {
      cache.set(handle, fetch(`${root}products/${encodeURIComponent(handle)}.js`, { headers: { Accept: 'application/json' } })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null));
    }
    return cache.get(handle);
  };

  const priceFrom = (cents) => t('price_from', 'ab %PRICE%').replace('%PRICE%', formatMoney(cents));
  const priceText = (product) => (product.price_varies ? priceFrom(product.price_min) : formatMoney(product.price_min));

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text) node.textContent = text;
    return node;
  };

  // Inline price after product entries
  links.forEach((link) => {
    const handle = handleFromLink(link);
    if (!handle || !link.closest('strong, b')) return;
    fetchProduct(handle).then((product) => {
      if (!product) return;
      const next = link.nextElementSibling;
      if (next && next.classList.contains('article-product-price')) return;
      // Non-breaking space: the price never wraps onto a line of its own starting with "·"
      link.after(el('span', 'article-product-price', `\u00a0· ${priceText(product)}`));
    });
  });

  // Hover card
  if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

  const SHOW_DELAY = 350;
  const HIDE_DELAY = 200;
  let card = null;
  let activeLink = null;
  let pendingLink = null;
  let showTimer = null;
  let hideTimer = null;

  const imageUrl = (src, width) => {
    if (!src) return null;
    const url = src.startsWith('//') ? `https:${src}` : src;
    return `${url}${url.includes('?') ? '&' : '?'}width=${width}`;
  };

  const renderPrice = (target, price, compareAt) => {
    target.textContent = '';
    target.append(price);
    if (compareAt) target.append(' ', el('s', 'article-product-preview__was', compareAt));
  };

  const hide = () => {
    clearTimeout(showTimer);
    if (card) {
      card.remove();
      card = null;
    }
    activeLink = null;
  };

  function scheduleHide() {
    pendingLink = null;
    clearTimeout(showTimer);
    clearTimeout(hideTimer);
    hideTimer = setTimeout(hide, HIDE_DELAY);
  }

  const addToCart = (variantId, button) => {
    button.disabled = true;
    button.classList.add('is-loading');
    fetch(`${root}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }] })
    })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        button.classList.remove('is-loading');
        if (!ok) {
          button.textContent = data.description || t('unavailable', 'Nicht verfügbar');
          setTimeout(() => {
            button.textContent = t('add_to_cart', 'In den Warenkorb');
            button.disabled = false;
          }, 3000);
          return;
        }
        button.textContent = `${t('added', 'Hinzugefügt')} ✓`;
        // Refreshes the header cart count and the cart drawer contents
        document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
        document.dispatchEvent(new CustomEvent('on:cart:add', { bubbles: true, detail: { variantId } }));

        // Then do what the theme does after add to cart (Theme settings > Cart)
        const after = window.theme && window.theme.settings && window.theme.settings.afterAddToCart;
        const drawer = document.querySelector('.js-cart-drawer');
        if (after === 'page') {
          window.location.href = (window.theme.routes && window.theme.routes.cart) || `${root}cart`;
        } else if (after === 'drawer' && drawer && typeof drawer.open === 'function') {
          const link = activeLink;
          hide();
          drawer.open(link);
        }
      })
      .catch(() => {
        button.classList.remove('is-loading');
        button.textContent = t('error', 'Das hat nicht geklappt. Bitte versuch es noch einmal.');
        button.disabled = false;
      });
  };

  const buildCard = (product, href) => {
    // Spans throughout: the card sits inside the article's paragraph, right after the link
    const wrap = el('span', 'article-product-preview');
    wrap.setAttribute('role', 'group');
    wrap.setAttribute('aria-label', product.title);

    const main = el('a', 'article-product-preview__main');
    main.href = href;
    main.tabIndex = -1; // the article link right before the card already leads here

    const img = imageUrl(product.featured_image, 160);
    if (img) {
      const image = el('img', 'article-product-preview__image');
      image.src = img;
      image.srcset = `${img} 1x, ${imageUrl(product.featured_image, 320)} 2x`;
      image.alt = '';
      image.width = 80;
      image.height = 80;
      main.appendChild(image);
    }

    const text = el('span', 'article-product-preview__text');
    text.appendChild(el('span', 'article-product-preview__title', product.title));
    const price = el('span', 'article-product-preview__price');
    const compareAt = product.compare_at_price_min > product.price_min ? formatMoney(product.compare_at_price_min) : null;
    renderPrice(price, priceText(product), compareAt);
    text.appendChild(price);
    text.appendChild(el('span', 'article-product-preview__price-note', t('price_note', 'inkl. MwSt.')));
    main.appendChild(text);
    wrap.appendChild(main);

    const available = product.variants.filter((v) => v.available);
    if (!available.length) {
      wrap.appendChild(el('span', 'article-product-preview__note', t('sold_out', 'Derzeit ausverkauft')));
    } else {
      let selected = available[0];
      const showVariant = (variant) => {
        const was = variant.compare_at_price > variant.price ? formatMoney(variant.compare_at_price) : null;
        renderPrice(price, formatMoney(variant.price), was);
      };
      const actions = el('span', 'article-product-preview__actions');
      const addButton = el('button', 'article-product-preview__add', t('add_to_cart', 'In den Warenkorb'));
      addButton.type = 'button';
      addButton.addEventListener('click', () => addToCart(selected.id, addButton));

      if (product.variants.length > 1) {
        const options = el('span', 'article-product-preview__options');
        options.setAttribute('role', 'radiogroup');
        options.setAttribute('aria-label', product.options.map((o) => o.name || o).join(' / '));
        product.variants.forEach((variant) => {
          const chip = el('button', 'article-product-preview__option', variant.title);
          chip.type = 'button';
          chip.setAttribute('role', 'radio');
          chip.setAttribute('aria-checked', String(variant === selected));
          chip.disabled = !variant.available;
          chip.addEventListener('click', () => {
            selected = variant;
            options.querySelectorAll('[role="radio"]').forEach((c) => c.setAttribute('aria-checked', String(c === chip)));
            showVariant(variant);
            addButton.disabled = false;
            addButton.textContent = t('add_to_cart', 'In den Warenkorb');
          });
          options.appendChild(chip);
        });
        actions.appendChild(options);
        if (product.price_varies) showVariant(selected);
      }

      actions.appendChild(addButton);
      wrap.appendChild(actions);
    }

    wrap.addEventListener('mouseenter', () => clearTimeout(hideTimer));
    wrap.addEventListener('mouseleave', scheduleHide);
    wrap.addEventListener('focusin', () => clearTimeout(hideTimer));
    wrap.addEventListener('focusout', (event) => {
      if (!wrap.contains(event.relatedTarget) && event.relatedTarget !== activeLink) scheduleHide();
    });
    return wrap;
  };

  // Top edge the card must stay below: the sticky header while it is on screen
  const topLimit = () => {
    const header = document.querySelector('.section-header');
    const bottom = header ? header.getBoundingClientRect().bottom : 0;
    return Math.max(16, bottom + 8);
  };

  const position = (node, link) => {
    const gutter = 16;
    const gap = 8;
    const rect = link.getClientRects()[0] || link.getBoundingClientRect();
    const width = node.offsetWidth;
    const height = node.offsetHeight;

    let left = rect.left + rect.width / 2 - width / 2;
    left = Math.max(gutter, Math.min(left, window.innerWidth - width - gutter));

    let top = rect.top - height - gap;
    const below = top < topLimit();
    node.classList.toggle('article-product-preview--below', below);
    if (below) top = rect.bottom + gap;

    // The card is absolutely positioned; convert viewport coordinates to its offsetParent's
    const parent = node.offsetParent || document.body;
    const parentRect = parent.getBoundingClientRect();
    node.style.left = `${left - parentRect.left - parent.clientLeft}px`;
    node.style.top = `${top - parentRect.top - parent.clientTop}px`;
  };

  const show = (link) => {
    const handle = handleFromLink(link);
    if (!handle) return;
    clearTimeout(hideTimer);
    if (activeLink === link) return;
    pendingLink = link;
    clearTimeout(showTimer);
    showTimer = setTimeout(() => {
      fetchProduct(handle).then((product) => {
        if (!product || pendingLink !== link) return;
        hide();
        activeLink = link;
        card = buildCard(product, link.href);
        link.after(card);
        position(card, link);
        // position() has already read the layout, so the fade-in transition runs from opacity 0
        card.classList.add('is-visible');
      });
    }, SHOW_DELAY);
  };

  let refocusing = false;

  links.forEach((link) => {
    const handle = handleFromLink(link);
    if (!handle) return;
    link.addEventListener('mouseenter', () => {
      fetchProduct(handle);
      show(link);
    });
    link.addEventListener('mouseleave', scheduleHide);
    link.addEventListener('focus', () => !refocusing && show(link));
    link.addEventListener('blur', (event) => {
      if (!card || !card.contains(event.relatedTarget)) scheduleHide();
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape' || !card) return;
    const link = activeLink;
    const focusWasInCard = card.contains(document.activeElement);
    hide();
    if (focusWasInCard && link) {
      refocusing = true;
      link.focus({ preventScroll: true });
      refocusing = false;
    }
  });
  window.addEventListener('scroll', () => activeLink && card && position(card, activeLink), { passive: true });
})();
